import { getCore } from '../../client/core/getCore';
import { getDefaultClient } from '../../client/defaultClient';
import { prefetchNoncesIfNeeded } from '../../utils/getNonce/prefetchNoncesIfNeeded/prefetchNoncesIfNeeded';
import { initializeAuth } from '../auth/initializeAuth';
import { isAuthStorageModeMismatch } from '../auth/isAuthStorageModeMismatch';
import { logout } from '../auth/logout';
import { setupCrossTabEventSync } from '../crossTabEventSync';
import { fetchProjectSettings } from '../projectSettings/fetchProjectSettings';
import { isCookieEnabled } from '../projectSettings/isCookieEnabled';
import { generateSessionKeys } from '../sessionKeys/generateSessionKeys';
import { raiseStateEvents } from '../state/raiseStateEvents';
import { initializeStorageSync } from '../storageSync';
import { refreshAuth } from '../user/refreshAuth';
import { restoreUserSharesForAllWalletAccounts } from '../waas/restoreUserSharesForAllWalletAccounts';
import { ClientAlreadyInitializedError } from './../../errors/ClientAlreadyInitializedError';
import {
  FETCH_PROJECT_SETTINGS_TRACKER_KEY,
  GENERATE_SESSION_KEYS_TRACKER_KEY,
  INITIALIZE_STORAGE_SYNC_TRACKER_KEY,
  REFRESH_USER_STATE_FROM_COOKIE_TRACKER_KEY,
} from './consts';

/**
 * Initializes the Dynamic client and all its modules and services.
 *
 * This function orchestrates the initialization of authentication, project settings,
 * session keys, and storage sync. It manages the initialization status and handles
 * any errors that occur during the process.
 *
 * You only need to call this function if you are not using `autoInitialize: true` in createDynamicClient.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves when initialization is complete.
 * @throws ClientAlreadyInitializedError If the client is already initialized.
 */
export const initializeClient = async (client = getDefaultClient()) => {
  const core = getCore(client);

  core.logger.debug(
    '[initializeClient] Initializing client. Current init status',
    core.state.get().initStatus
  );

  if (core.state.get().initStatus !== 'uninitialized') {
    throw new ClientAlreadyInitializedError();
  }

  core.state.set({ initStatus: 'in-progress' });

  initializeAuth(client);

  raiseStateEvents(client);

  setupCrossTabEventSync(client);

  const initializeStorageSyncPromise = initializeStorageSync(client);

  const fetchProjectSettingsPromise: Promise<void> =
    initializeStorageSyncPromise.then(async () => {
      if (!core.state.get().projectSettings) {
        await fetchProjectSettings(client);
      }
    });

  // Prefetch nonces in the background so they are ready when a wallet
  // signing operation starts. Not tracked — failures are non-blocking.
  void fetchProjectSettingsPromise
    .then(() => prefetchNoncesIfNeeded(client))
    .catch((error) => {
      core.logger.error('Failed to prefetch nonces:', error);
    });

  /**
   * Generate session keys if they don't exist
   */
  const generateSessionKeysPromise: Promise<void> =
    fetchProjectSettingsPromise.then(async () => {
      if (!core.state.get().sessionKeys) {
        await generateSessionKeys(client);
      }
    });

  /**
   * Refresh user state on initialization when cookie is enabled.
   * This is necessary for supporting multiple subdomain domains.
   *
   * When a user logs in on sub1.example.com, they will have a cookie set. However, when they access
   * sub2.example.com, the cookie will be present but the user state will not be present.
   * We need to fetch the user to access the expiration date of the cookie on the new subdomain.
   *
   * This is also needed in case a user logs in with a new user on sub1.example.com and then accesses
   * sub2.example.com that had the original logged-in user. We need to fetch the user to refresh
   * the user state with the correct logged-in user.
   */
  const refreshUserStateFromCookiePromise: Promise<void> =
    fetchProjectSettingsPromise.then(async () => {
      if (!isCookieEnabled(client)) {
        return;
      }

      // If switching from header auth to cookie auth, force logout
      // the user so they re-authenticate under the new auth mode.
      if (isAuthStorageModeMismatch(client)) {
        await logout(client);
        return;
      }

      /**
       * When cookies are enabled, we need to refresh the user as part of the initialization process
       * to ensure the user is logged in and sync the auth state with the server.
       *
       * This call can fail with a 401 error if the user is not logged in and the SDK should finish its initialization process
       * without throwing an error because that is an expected behavior.
       */
      await refreshAuth(client).catch(async (error) => {
        if (error.status === 401) {
          if (client.user) {
            await logout(client);
          }
        } else {
          throw error;
        }
      });
    });

  core.initTrack.track({
    name: INITIALIZE_STORAGE_SYNC_TRACKER_KEY,
    promise: initializeStorageSyncPromise,
  });

  core.initTrack.track({
    name: FETCH_PROJECT_SETTINGS_TRACKER_KEY,
    promise: fetchProjectSettingsPromise,
  });

  core.initTrack.track({
    name: GENERATE_SESSION_KEYS_TRACKER_KEY,
    promise: generateSessionKeysPromise,
  });

  core.initTrack.track({
    name: REFRESH_USER_STATE_FROM_COOKIE_TRACKER_KEY,
    promise: refreshUserStateFromCookiePromise,
  });

  try {
    await core.initTrack.waitForAll();

    core.state.set({ initStatus: 'finished' });
  } catch (error) {
    core.state.set({ initStatus: 'failed' });

    throw error;
  }

  if (client.user) {
    void restoreUserSharesForAllWalletAccounts(client);
  }
};
